import type { ConditionItem } from '-/Base/ConditionBox'
import { API_CONDITION_LINK_TYPE, API_CONDITION_TYPE } from '@/constant/api'
import { deepMerge, isArray, isBoolean, isNumber, isString } from '@bd/vue3-utils'
import { computed } from 'vue'

/**
 * 判断一个值是否“有意义”（可作为查询条件值）。
 * 说明：
 * - `null/undefined`：无意义
 * - `number`：`NaN` 无意义；0 等正常数值有意义
 * - `string`：去空格后为空无意义
 * - `array`：空数组无意义
 * - 其他（对象/日期/dayjs 等）：只要非空就认为有意义
 */
const hasMeaningfulValue = (value: unknown): boolean => {
  if (value === undefined || value === null) return false

  if (isNumber(value)) return !Number.isNaN(value)
  if (isString(value)) return value.trim() !== ''
  if (isBoolean(value)) return true
  if (isArray(value)) return value.length > 0

  // 例如：Date、dayjs、对象结构等
  return true
}

/**
 * 校验条件值是否有效（用于决定是否生成条件项）。
 * - `between`：支持区间/单边区间
 *   - 两端都为空：无效
 *   - 只有一端有值：有效（后续会转换成 `gt` 或 `lt`）
 * - `in`：期望为数组；空数组无效
 * - 其他：使用通用“有意义”判断
 */
const isValidConditionValue = (value: unknown, atom?: ConditionType): boolean => {
  if (atom === API_CONDITION_TYPE.BETWEEN) {
    if (!isArray(value) || value.length !== 2) return false
    const [start, end] = value
    return hasMeaningfulValue(start) || hasMeaningfulValue(end)
  }

  if (atom === API_CONDITION_TYPE.IN) {
    return isArray(value) && value.length > 0
  }

  return hasMeaningfulValue(value)
}

/**
 * 归一化条件值与条件类型。
 * - `between`：若仅一端有值，自动降级为 `gt`/`lt`
 * - `in`：数组=>字符串
 */
const normalizeValueAndType = (
  value: unknown,
  atom: ConditionType
): {
  conditionValue: unknown
  conditionType: ConditionType
} => {
  let normalizedValue: unknown = value
  let normalizedAtom: ConditionType = atom

  if (atom === API_CONDITION_TYPE.BETWEEN && isArray(value) && value.length === 2) {
    const [start, end] = value
    const hasStart = hasMeaningfulValue(start)
    const hasEnd = hasMeaningfulValue(end)

    // 只填起始值：视为“大于”
    if (hasStart && !hasEnd) {
      normalizedValue = start
      normalizedAtom = API_CONDITION_TYPE.GT
    }
    // 只填结束值：视为“小于”
    else if (!hasStart && hasEnd) {
      normalizedValue = end
      normalizedAtom = API_CONDITION_TYPE.LT
    }
  }

  if (atom === API_CONDITION_TYPE.IN) {
    // 仅在数组场景下做 join，避免 value 为 string/number 时抛错
    if (isArray(value)) normalizedValue = value.join(',')
  }

  return {
    conditionValue: normalizedValue,
    conditionType: normalizedAtom
  }
}

export const useConditions = (schemas: ConditionItem[] = []) => {
  const schemaMap = computed(() => {
    const map: Record<string, ConditionItem> = {}
    schemas.forEach(item => {
      map[item.dataIndex as string] = item
    })
    return map
  })
  const genConditions = (params: Record<string, any>) => {
    const res: BasicCondition[] = []
    Object.entries(params).forEach(([key, value]) => {
      const schema = schemaMap.value[key]
      const conditionType = schema?.atom ?? API_CONDITION_TYPE.EQUAL
      const conditionLinkType = API_CONDITION_LINK_TYPE.AND

      if (isValidConditionValue(value, conditionType)) {
        res.push({
          conditionKeyName: key,
          conditionLinkType,
          ...normalizeValueAndType(value, conditionType)
        })
      }
    })
    return res
  }
  return {
    genConditions
  }
}

export const mergeReqConditions = (target: BasicPageReq, source: Partial<BasicCondition>[]) => {
  const conditions: BasicCondition[] = []
  source.forEach(item => {
    const { conditionType = 'equal', conditionLinkType = 'and', conditionValue, conditionKeyName = '' } = item
    if (!isValidConditionValue(conditionValue, conditionType)) return
    conditions.push({
      conditionKeyName,
      conditionLinkType,
      ...normalizeValueAndType(conditionValue, conditionType)
    })
  })
  return deepMerge(target, {
    conditions
  }) as BasicPageReq
}

export const genSimpleConditionItem = (
  conditionKeyName: string,
  conditionValue: unknown,
  conditionType: ConditionType = API_CONDITION_TYPE.EQUAL,
  conditionLinkType: API_CONDITION_LINK_TYPE = API_CONDITION_LINK_TYPE.AND
): BasicCondition | null => {
  if (!isValidConditionValue(conditionValue, conditionType)) return null
  return {
    conditionKeyName,
    conditionLinkType,
    ...normalizeValueAndType(conditionValue, conditionType)
  }
}
